/**
 * ProgramStaffTab - Manage staff assignments for a program
 * Supports roles: Director, Manager, Coach, Assistant Coach, General Manager
 */
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { 
  Users, UserPlus, Trash2, Edit, Loader2, 
  Crown, Briefcase, User, Shield, Star
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const ROLE_CONFIG = {
  director: { label: "Director", plural: "Directors", icon: Crown, color: "bg-purple-100 text-purple-700" },
  general_manager: { label: "General Manager", plural: "General Managers", icon: Briefcase, color: "bg-blue-100 text-blue-700" },
  manager: { label: "Manager", plural: "Managers", icon: Shield, color: "bg-indigo-100 text-indigo-700" },
  head_coach: { label: "Head Coach", plural: "Head Coaches", icon: Star, color: "bg-emerald-100 text-emerald-700" },
  team_manager: { label: "Team Manager", plural: "Team Managers", icon: Shield, color: "bg-indigo-100 text-indigo-700" },
  coach: { label: "Coach", plural: "Coaches", icon: Star, color: "bg-emerald-100 text-emerald-700" },
  assistant_coach: { label: "Assistant Coach", plural: "Assistant Coaches", icon: User, color: "bg-slate-100 text-slate-700" }
};

const ProgramStaffTab = ({ programId, token, API }) => {
  const [staff, setStaff] = useState([]);
  const [availableStaff, setAvailableStaff] = useState({ coaches: [], staff: [] });
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  
  // Dialog states
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [editingAssignment, setEditingAssignment] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/programs/${programId}/staff`, { headers });
      setStaff(response.data.staff || []);
      setAvailableRoles(response.data.available_roles || []);
    } catch (error) {
      console.error("Error fetching staff:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableStaff = async () => {
    try {
      const response = await axios.get(`${API}/programs/${programId}/available-staff`, { headers });
      setAvailableStaff({
        coaches: response.data.coaches || [],
        staff: response.data.staff || []
      });
    } catch (error) {
      console.error("Error fetching available staff:", error);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, [programId]);

  const handleOpenAssignDialog = () => {
    fetchAvailableStaff();
    setSelectedStaff(null);
    setSelectedRole("");
    setAssignDialogOpen(true);
  };

  const handleAssignStaff = async () => {
    if (!selectedStaff || !selectedRole) {
      toast.error("Please select a staff member and role");
      return;
    }

    setAssigning(true);
    try {
      await axios.post(
        `${API}/programs/${programId}/staff`,
        {
          staff_id: selectedStaff.staff_id,
          staff_type: selectedStaff.staff_type,
          role: selectedRole
        },
        { headers }
      );
      toast.success("Staff assigned successfully");
      setAssignDialogOpen(false);
      fetchStaff();
    } catch (error) {
      console.error("Error assigning staff:", error);
      toast.error(error.response?.data?.detail || "Failed to assign staff");
    } finally {
      setAssigning(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!editingAssignment || !selectedRole) return;

    setAssigning(true);
    try {
      await axios.put(
        `${API}/programs/${programId}/staff/${editingAssignment.assignment_id}`,
        { role: selectedRole },
        { headers }
      );
      toast.success("Role updated successfully");
      setEditDialogOpen(false);
      fetchStaff();
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error("Failed to update role");
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveStaff = async (assignmentId) => {
    if (!confirm("Are you sure you want to remove this staff member from the program?")) {
      return;
    }

    try {
      await axios.delete(`${API}/programs/${programId}/staff/${assignmentId}`, { headers });
      toast.success("Staff removed from program");
      fetchStaff();
    } catch (error) {
      console.error("Error removing staff:", error);
      toast.error("Failed to remove staff");
    }
  };

  const openEditDialog = (assignment) => {
    setEditingAssignment(assignment);
    setSelectedRole(assignment.role);
    setEditDialogOpen(true);
  };

  // Group staff by role
  const staffByRole = staff.reduce((acc, member) => {
    const role = member.role || "unassigned";
    if (!acc[role]) acc[role] = [];
    acc[role].push(member);
    return acc;
  }, {});

  // Order roles for display
  const roleOrder = ["director", "general_manager", "manager", "coach", "assistant_coach"];
  const sortedRoles = [...roleOrder.filter(role => staffByRole[role]?.length > 0), ...Object.keys(staffByRole).filter(role => !roleOrder.includes(role))];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="program-section program-staff" data-program-section="staff">
      {/* Header */}
      <div className="program-section-heading">
        <div>
          <h2>Staff</h2>
          <p className="text-sm text-slate-500">{staff.length} staff members assigned</p>
        </div>
        <Button onClick={handleOpenAssignDialog} className="rounded-xl">
          <UserPlus className="w-4 h-4 mr-2" />
          Assign Staff
        </Button>
      </div>

      {/* Staff List by Role */}
      {sortedRoles.length > 0 ? (
        <div className="space-y-4">
          {sortedRoles.map(role => {
            const config = ROLE_CONFIG[role] || { label: role, icon: User, color: "bg-slate-100 text-slate-700" };
            const Icon = config.icon;
            
            return (
              <Card key={role} className="program-staff-list">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${config.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    {config.plural || (role === "unassigned" ? "Role not specified" : `Role: ${role}`)} ({staffByRole[role].length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {staffByRole[role].map(member => (
                      <div 
                        key={member.assignment_id}
                        className="program-staff-row"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center text-sm font-medium">
                            {member.avatar_url ? (
                              <img 
                                src={member.avatar_url} 
                                alt="" 
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {member.first_name} {member.last_name}
                            </p>
                          </div>
                        </div>
                        <p className="program-staff-email">{member.email || 'Email unavailable'}</p>
                        <div className="flex items-center gap-2">
                          <Badge className={`${config.color} rounded-lg`}>
                            {member.role ? config.label : 'Role not specified'}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openEditDialog(member)}
                            aria-label={`Edit role for ${member.first_name} ${member.last_name}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveStaff(member.assignment_id)}
                            aria-label={`Remove ${member.first_name} ${member.last_name} from program`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="program-staff-list">
          <CardContent className="program-empty-state">
            <Users className="w-12 h-12 mx-auto mb-3 text-slate-200" />
            <p className="text-slate-500 mb-4">No staff assigned to this program yet</p>
            <Button onClick={handleOpenAssignDialog} variant="outline" className="rounded-xl">
              <UserPlus className="w-4 h-4 mr-2" />
              Assign First Staff Member
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Assign Staff Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Staff to Program</DialogTitle>
            <DialogDescription>
              Select a staff member and assign them a role
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Staff Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Staff Member</label>
              <Select
                value={selectedStaff ? `${selectedStaff.staff_type}-${selectedStaff.staff_id}` : ""}
                onValueChange={(value) => {
                  const allStaff = [...availableStaff.coaches, ...availableStaff.staff];
                  const found = allStaff.find(s => `${s.staff_type}-${s.staff_id}` === value);
                  setSelectedStaff(found);
                }}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Choose a staff member..." />
                </SelectTrigger>
                <SelectContent>
                  {availableStaff.coaches.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-500">Coaches</div>
                      {availableStaff.coaches.map(c => (
                        <SelectItem key={c.staff_id} value={`coach-${c.staff_id}`}>
                          {c.first_name} {c.last_name} ({c.email})
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {availableStaff.staff.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-500">Staff</div>
                      {availableStaff.staff.map(s => (
                        <SelectItem key={s.staff_id} value={`staff-${s.staff_id}`}>
                          {s.first_name} {s.last_name} ({s.email})
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {availableStaff.coaches.length === 0 && availableStaff.staff.length === 0 && (
                    <div className="px-2 py-4 text-center text-sm text-slate-500">
                      No available staff members
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Role Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Assign Role</label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Choose a role..." />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map(role => {
                    const config = ROLE_CONFIG[role] || { label: role };
                    return (
                      <SelectItem key={role} value={role}>
                        {config.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignStaff} 
              disabled={!selectedStaff || !selectedRole || assigning}
            >
              {assigning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Assign Staff
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update Staff Role</DialogTitle>
            <DialogDescription>
              Change the role for {editingAssignment?.first_name} {editingAssignment?.last_name}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <label className="text-sm font-medium">New Role</label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger className="rounded-xl mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableRoles.map(role => {
                  const config = ROLE_CONFIG[role] || { label: role };
                  return (
                    <SelectItem key={role} value={role}>
                      {config.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateRole} disabled={assigning}>
              {assigning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Update Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgramStaffTab;
